import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Checkbox from "../../../components/BaseUI/Checkbox";
import Select from "../../../components/BaseUI/Select";
import { FiPlus, FiEdit2, FiTrash2, FiCreditCard } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";
const PAGE_PATH = "/account/bank-accounts";

const emptyAccount = (overrides = {}) => ({
  label: "",
  bankRecipientName: "",
  bankBranchName: "",
  bankIban: "",
  isDefault: false,
  ...overrides,
});

const tableColumns = [
  { name: "Etiket", dataType: "text", id: 0 },
  { name: "Alıcı", dataType: "text", id: 1 },
  { name: "Banka / Şube", dataType: "text", id: 2 },
  { name: "IBAN", dataType: "text", id: 3 },
  // { name: "Varsayılan", dataType: "text", id: 4 },
  { name: "İşlemler", id: 5, sortable: false },
];

const BankAccounts = () => {
  useTitle("Rovion MES - Banka Hesapları");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const isAdmin = currentUser?.role?.toLowerCase() === "admin";
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      PAGE_PATH,
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [accounts, setAccounts] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [systemCompanies, setSystemCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    currentUser?.companyId || "",
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState(emptyAccount());

  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );
  const addToast = useCallback((message, type = "success") => {
    setToasts((p) => [...p, { id: Date.now(), message, type }]);
  }, []);
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const fetchAccounts = useCallback(async () => {
    const companyId = isAdmin ? selectedCompanyId : currentUser?.companyId;
    if (!companyId) {
      setAccounts([]);
      setCompanyName("");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = isAdmin ? { companyId } : undefined;
      const { data } = await axios.get(
        `${API_URL}/api/system-companies/me/bank-accounts/manage`,
        { ...authHeaders(), params },
      );
      setAccounts(data.bankAccounts || []);
      setCompanyName(data.companyName || "");
    } catch (error) {
      addToast(
        error.response?.data?.message || "Banka hesapları yüklenemedi",
        "danger",
      );
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [
    authHeaders,
    addToast,
    isAdmin,
    selectedCompanyId,
    currentUser?.companyId,
  ]);

  useEffect(() => {
    if (!isAdmin) return;
    const loadCompanies = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/system-companies`,
          authHeaders(),
        );
        const companies = data.companies || data || [];
        setSystemCompanies(companies);
        if (!selectedCompanyId && companies.length > 0) {
          const fallback =
            currentUser?.companyId || companies[0]._id || companies[0].id;
          setSelectedCompanyId(fallback || "");
        }
      } catch {
        addToast("Sistem firmaları yüklenemedi", "danger");
      }
    };
    loadCompanies();
  }, [isAdmin, authHeaders, addToast, currentUser?.companyId]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const persistAccounts = async (nextAccounts, successMessage) => {
    setSaving(true);
    try {
      const payload = {
        ...(isAdmin && selectedCompanyId
          ? { companyId: selectedCompanyId }
          : {}),
        bankAccounts: nextAccounts.map((a, index) => ({
          ...(a._id ? { _id: a._id } : {}),
          label: (a.label || `Hesap ${index + 1}`).trim(),
          bankRecipientName: (a.bankRecipientName || "").trim(),
          bankBranchName: (a.bankBranchName || "").trim(),
          bankIban: (a.bankIban || "").trim(),
          isDefault: !!a.isDefault,
        })),
      };
      const { data } = await axios.put(
        `${API_URL}/api/system-companies/me/bank-accounts`,
        payload,
        authHeaders(),
      );
      setAccounts(data.bankAccounts || payload.bankAccounts);
      addToast(successMessage || "Banka hesapları kaydedildi", "success");
      return true;
    } catch (error) {
      addToast(
        error.response?.data?.message || "Banka hesapları kaydedilemedi",
        "danger",
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setEditingIndex(null);
    setFormData(
      emptyAccount({
        isDefault: accounts.length === 0,
        label: `Hesap ${accounts.length + 1}`,
      }),
    );
    setSidebarOpened(true);
  };

  const openEdit = (account, index) => {
    setEditingIndex(index);
    setFormData({
      _id: account._id,
      label: account.label || "",
      bankRecipientName: account.bankRecipientName || "",
      bankBranchName: account.bankBranchName || "",
      bankIban: account.bankIban || "",
      isDefault: !!account.isDefault,
    });
    setSidebarOpened(true);
  };

  const handleClose = () => {
    setSidebarOpened(false);
    setEditingIndex(null);
    setFormData(emptyAccount());
  };

  const handleSave = async () => {
    if (!formData.bankRecipientName.trim()) {
      addToast("Alıcı adı zorunludur", "warning");
      return;
    }
    if (!formData.bankBranchName.trim()) {
      addToast("Banka / şube zorunludur", "warning");
      return;
    }
    if (!formData.bankIban.trim()) {
      addToast("IBAN zorunludur", "warning");
      return;
    }

    let next = [...accounts];
    const entry = {
      ...(formData._id ? { _id: formData._id } : {}),
      label: formData.label.trim() || `Hesap ${accounts.length + 1}`,
      bankRecipientName: formData.bankRecipientName.trim(),
      bankBranchName: formData.bankBranchName.trim(),
      bankIban: formData.bankIban.trim(),
      isDefault: !!formData.isDefault,
    };

    if (editingIndex === null) {
      next.push(entry);
    } else {
      next[editingIndex] = { ...next[editingIndex], ...entry };
    }

    if (entry.isDefault) {
      next = next.map((a, i) => ({
        ...a,
        isDefault:
          editingIndex === null ? i === next.length - 1 : i === editingIndex,
      }));
    } else if (next.length > 0 && !next.some((a) => a.isDefault)) {
      next[0] = { ...next[0], isDefault: true };
    }

    const ok = await persistAccounts(
      next,
      editingIndex === null ? "Hesap eklendi" : "Hesap güncellendi",
    );
    if (ok) handleClose();
  };

  const handleDelete = async (index) => {
    let next = accounts.filter((_, i) => i !== index);
    if (next.length > 0 && !next.some((a) => a.isDefault)) {
      next[0] = { ...next[0], isDefault: true };
    }
    await persistAccounts(next, "Hesap silindi");
  };

  const handleSetDefault = async (index) => {
    const next = accounts.map((a, i) => ({
      ...a,
      isDefault: i === index,
    }));
    await persistAccounts(next, "Varsayılan hesap güncellendi");
  };

  return (
    <AppPage
      title="Banka Hesapları"
      description={
        companyName
          ? `${companyName} için teklif PDF banka hesaplarını yönetin`
          : "Teklif PDF banka hesaplarını yönetin"
      }
      actions={
        canWrite ? (
          <Button
            appearance="primary-animated"
            icon={<FiPlus />}
            onClick={openCreate}
          >
            Hesap Ekle
          </Button>
        ) : null
      }
    >
      {isAdmin && systemCompanies.length > 0 && (
        <CardContainer className="mb-3">
          <Label className="mb-0">Sistem Firması</Label>
          <Select
            unique
            options={systemCompanies.map((c) => ({
              value: String(c._id || c.id),
              label: c.name || c.code || "Firma",
            }))}
            selected={selectedCompanyId ? [selectedCompanyId] : []}
            onSelected={(values) =>
              setSelectedCompanyId(values?.[0]?.value || "")
            }
            placeholder="Firma seçin"
          />
        </CardContainer>
      )}
      <CardContainer>
        <Table columns={tableColumns} loading={loading} search={false}>
          {accounts.length === 0 && !loading ? (
            <Tr>
              <Td colSpan={6}>
                <div className="text-center py-5 text-muted small">
                  <FiCreditCard size={40} className="mb-2 d-block mx-auto" />
                  Henüz banka hesabı yok.
                  {canWrite ? " Yeni hesap ekleyebilirsiniz." : ""}
                </div>
              </Td>
            </Tr>
          ) : (
            accounts.map((account, index) => (
              <Tr key={account._id || index} data={account}>
                <Td>
                  <strong>{account.label || `Hesap ${index + 1}`}</strong>
                </Td>
                <Td>{account.bankRecipientName || "—"}</Td>
                <Td>{account.bankBranchName || "—"}</Td>
                <Td>
                  <span className="small">{account.bankIban || "—"}</span>
                </Td>
                {/* <Td>
                  {account.isDefault ? (
                    <Lozenge appearance="success-subtle" isBold>
                      Varsayılan
                    </Lozenge>
                  ) : canWrite ? (
                    <Button
                      appearance="subtle"
                      onClick={() => handleSetDefault(index)}
                    >
                      Varsayılan Yap
                    </Button>
                  ) : (
                    "—"
                  )}
                </Td> */}
                <Td>
                  {canWrite ? (
                    <div className="d-flex gap-2">
                      <IconButton
                        icon={<FiEdit2 />}
                        label="Düzenle"
                        appearance="subtle"
                        shape="circle"
                        onClick={() => openEdit(account, index)}
                      />
                      <IconButton
                        icon={<FiTrash2 />}
                        label="Sil"
                        appearance="subtle"
                        shape="circle"
                        onClick={() => handleDelete(index)}
                      />
                    </div>
                  ) : (
                    "—"
                  )}
                </Td>
              </Tr>
            ))
          )}
        </Table>
      </CardContainer>

      <SidebarContainer
        show={sidebarOpened}
        title={editingIndex === null ? "Hesap Ekle" : "Hesabı Düzenle"}
        onClose={handleClose}
      >
        <div className="d-flex flex-column" style={{ height: "100%" }}>
          <div className="flex-grow-1 overflow-auto pe-1">
            <div className="mt-2">
              <Label className="mb-0">Hesap Etiketi</Label>
              <Input
                type="text"
                value={formData.label}
                onChange={(v) => setFormData((p) => ({ ...p, label: v }))}
                placeholder="Hesap etiketi"
              />
            </div>
            <div className="mt-3">
              <Label className="mb-0">Alıcı Adı *</Label>
              <Input
                type="text"
                value={formData.bankRecipientName}
                onChange={(v) =>
                  setFormData((p) => ({ ...p, bankRecipientName: v }))
                }
                placeholder="Alıcı adı"
              />
            </div>
            <div className="mt-3">
              <Label className="mb-0">Banka / Şube *</Label>
              <Input
                type="text"
                value={formData.bankBranchName}
                onChange={(v) =>
                  setFormData((p) => ({ ...p, bankBranchName: v }))
                }
                placeholder="Banka / şube"
              />
            </div>
            <div className="mt-3">
              <Label className="mb-0">IBAN *</Label>
              <Input
                type="text"
                value={formData.bankIban}
                onChange={(v) => setFormData((p) => ({ ...p, bankIban: v }))}
                placeholder="IBAN"
              />
            </div>
            {/* <div className="mt-3">
              <Checkbox
                value="default-account"
                label="Varsayılan hesap"
                isChecked={!!formData.isDefault}
                onChange={({ checked }) =>
                  setFormData((p) => ({ ...p, isDefault: !!checked }))
                }
              />
            </div> */}
          </div>
          <div className="mt-4 d-flex gap-2 justify-content-end">
            <Button appearance="danger-subtle" onClick={handleClose}>
              İptal
            </Button>
            <Button
              appearance="primary-animated"
              onClick={handleSave}
              loading={saving}
            >
              Kaydet
            </Button>
          </div>
        </div>
      </SidebarContainer>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(BankAccounts);
